/**
 * Mission Control Centre
 *
 * Unified admin dashboard consolidating barcode reviews, audit missions, and
 * stock reconciliation into a tabbed interface following the SKC Staging
 * Dashboard pattern (glassmorphism header, GlowCards, dark fintech aesthetic).
 *
 * Tabs:
 * - Barcode Review: Approve/reject barcode submissions from pickers
 * - Audit Missions: Create & monitor barcode audit missions
 * - Stock Recon: View recon sessions and scanned items
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Fade,
  LinearProgress,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  CardActionArea,
  Drawer,
  Divider,
  Collapse,
  alpha,
  TablePagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  QrCodeScanner as BarcodeIcon,
  Assignment as MissionIcon,
  Inventory as ReconIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Close as CloseIcon,
  SkipNext as SkipNextIcon,
  HourglassEmpty as PendingItemIcon,
  AutoAwesome as AlreadyBarcodedIcon,
  SwapHoriz as SwapHorizIcon,
  AddCircle as AddCircleIcon,
  ReceiptLong as ReceiptLongIcon,
  Inventory2 as DashboardIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlaylistAddCheck as ScannedIcon,
} from '@mui/icons-material';
import { Title } from 'react-admin';
import config from '../config';
import { useAdminTheme, AdminColors } from '../admin-theme';
import { useScrollToListTop } from '../hooks/useScrollToListTop';
import { getValidToken } from '../services/tokenRefreshService';

// ============================================================================
// CSS Keyframes (injected once)
// ============================================================================

const _keyframesInjected = (() => {
  if (typeof document === 'undefined') return true;
  const id = 'inv-dash-keyframes';
  if (document.getElementById(id)) return true;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    @keyframes inv-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    @keyframes inv-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes inv-count-pop { 0% { transform: scale(1); } 50% { transform: scale(1.15); } 100% { transform: scale(1); } }
  `;
  document.head.appendChild(style);
  return true;
})();

// ============================================================================
// Types
// ============================================================================

// -- Barcode Review types (mirrored from ProductReview/types.ts) --

interface ReviewProduct {
  id: string;
  name: string;
  sku: string;
  barcode: string | null;
  brand: string | null;
}

interface ReviewUser {
  id: string;
  firstName: string;
  lastName: string;
}

interface ReviewOrder {
  id: string;
  number: string;
}

type ReviewStatus = 'pending' | 'approved' | 'rejected';
type ReviewType = 'barcode_add' | 'barcode_reassign' | 'new_product';

interface BarcodeReview {
  id: string;
  productId: string | null;
  orderId: string | null;
  orderLineId: string | null;
  auditItemId: string | null;
  scannedBarcode: string;
  photoUrl: string | null;
  photoDownloadUrl: string | null;
  status: ReviewStatus;
  reviewType: ReviewType;
  submittedBy: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  wrongProductId: string | null;
  quantityFound: number | null;
  suggestedProductName: string | null;
  externalProductId: string | null;
  createdAt: string;
  updatedAt: string;
  product?: ReviewProduct;
  wrongProduct?: ReviewProduct;
  submitter?: ReviewUser;
  reviewer?: ReviewUser;
  order?: ReviewOrder;
}

interface ReviewListResponse {
  data: BarcodeReview[];
  total: number;
  page: number;
  limit: number;
}

// -- Audit Mission types (mirrored from BarcodeAudit/types.ts) --

type MissionStatus = 'pending' | 'active' | 'paused' | 'completed' | 'expired' | 'cancelled';
type MissionSource = 'auto' | 'manual' | 'recon';
type AuditItemStatus = 'pending' | 'scanned' | 'out_of_stock' | 'skipped' | 'already_barcoded';

interface AuditMissionItem {
  id: string;
  missionId: string;
  productId: string;
  status: AuditItemStatus;
  scannedBarcode: string | null;
  photoUrl: string | null;
  reviewId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  product: {
    id: string;
    name: string;
    sku: string;
    brand: string | null;
    barcode: string | null;
    isPopular: boolean;
    attachments: Array<{ downloadUrl: string | null; thumbnailUrl: string | null }>;
    categoryRelation: { name: string } | null;
  } | null;
}

interface AuditMission {
  id: string;
  storeId: string;
  assignedTo: string;
  status: MissionStatus;
  source: MissionSource;
  createdBy: string | null;
  totalItems: number;
  scannedCount: number;
  skippedCount: number;
  startedAt: string | null;
  pausedAt: string | null;
  completedAt: string | null;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  picker?: { id: string; firstName: string; lastName: string } | null;
  creator?: { id: string; firstName: string; lastName: string } | null;
  items?: AuditMissionItem[];
}

interface AuditMissionsResponse {
  data: AuditMission[];
  total: number;
  page: number;
  limit: number;
}

interface AuditStats {
  totalMissions: number;
  activeMissions: number;
  completedMissions: number;
  totalItemsScanned: number;
  totalItemsSkipped: number;
  avgCompletionRate: number;
}

interface OnlinePicker {
  id: string;
  firstName: string;
  lastName: string;
  storeId: string;
  storeName: string;
}

type MissionFilterStatus = MissionStatus | 'all';
type MissionFilterSource = MissionSource | 'all';
type ReviewFilterStatus = ReviewStatus | 'all';

// ============================================================================
// API Helper
// ============================================================================

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = await getValidToken();
  const res = await fetch(`${config.API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `API ${res.status}: ${res.statusText}`);
  }
  return res.json();
}

// ============================================================================
// Helpers
// ============================================================================

function formatName(user?: { firstName: string; lastName: string } | null): string {
  if (!user) return 'Unknown';
  return `${user.firstName} ${user.lastName}`.trim();
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function timeRemaining(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const hrs = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return hrs > 0 ? `${hrs}h ${mins}m left` : `${mins}m left`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-ZA');
}

// ============================================================================
// Tab Panel
// ============================================================================

function TabPanel({ children, value, index }: { children?: React.ReactNode; index: number; value: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Fade in timeout={300}><Box sx={{ py: 3 }}>{children}</Box></Fade>}
    </div>
  );
}

// ============================================================================
// Glow Summary Card
// ============================================================================

interface GlowCardProps {
  icon: React.ReactElement;
  label: string;
  value: number | string;
  color: string;
  subtitle?: string;
  pulse?: boolean;
  onClick?: () => void;
  active?: boolean;
}

function GlowCard({ icon, label, value, color, subtitle, pulse, onClick, active }: GlowCardProps) {
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2.5,
        height: '100%',
        bgcolor: active ? alpha(color, 0.08) : AdminColors.bgCard,
        border: `1px solid ${active ? alpha(color, 0.5) : AdminColors.border}`,
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          bgcolor: AdminColors.bgElevated,
          borderColor: alpha(color, 0.4),
          boxShadow: `0 0 30px ${alpha(color, 0.15)}, inset 0 0 30px ${alpha(color, 0.05)}`,
          transform: 'translateY(-2px)',
        },
        '&::before': pulse ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          animation: 'inv-shimmer 2s linear infinite',
          backgroundSize: '200% 100%',
        } : {},
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(color, 0.12),
          color,
          flexShrink: 0,
          transition: 'all 0.3s',
          boxShadow: `0 0 15px ${alpha(color, 0.1)}`,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 26 } } as any)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: AdminColors.textMuted, display: 'block', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            color: AdminColors.textPrimary,
            fontWeight: 800,
            lineHeight: 1.2,
            mt: 0.25,
            ...(pulse ? { animation: 'inv-count-pop 0.5s ease' } : {}),
          }}
        >
          {typeof value === 'number' ? formatNumber(value) : value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: AdminColors.textSecondary, fontSize: '0.7rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Review Type Config
// ============================================================================

const REVIEW_TYPE_CONFIG: Record<ReviewType, { label: string; color: string; icon: React.ReactNode }> = {
  barcode_add: { label: 'Add Barcode', color: AdminColors.info, icon: <BarcodeIcon sx={{ fontSize: 14 }} /> },
  barcode_reassign: { label: 'Reassign', color: AdminColors.warning, icon: <SwapHorizIcon sx={{ fontSize: 14 }} /> },
  new_product: { label: 'New Product', color: '#8B5CF6', icon: <AddCircleIcon sx={{ fontSize: 14 }} /> },
};

// ============================================================================
// Mission Status Config
// ============================================================================

const MISSION_STATUS_CONFIG: Record<MissionStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: AdminColors.textSecondary },
  active: { label: 'Active', color: AdminColors.info },
  paused: { label: 'Paused', color: AdminColors.warning },
  completed: { label: 'Completed', color: AdminColors.success },
  expired: { label: 'Expired', color: AdminColors.error },
  cancelled: { label: 'Cancelled', color: AdminColors.textMuted },
};

const MISSION_SOURCE_CONFIG: Record<MissionSource, { label: string; color: string }> = {
  auto: { label: 'Auto', color: AdminColors.info },
  manual: { label: 'Manual', color: AdminColors.warning },
  recon: { label: 'Recon', color: AdminColors.purple },
};

const ITEM_STATUS_CONFIG: Record<AuditItemStatus, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: 'Pending', color: AdminColors.textSecondary, icon: <PendingItemIcon sx={{ fontSize: 16 }} /> },
  scanned: { label: 'Scanned', color: AdminColors.success, icon: <ApproveIcon sx={{ fontSize: 16 }} /> },
  out_of_stock: { label: 'Out of Stock', color: AdminColors.error, icon: <RejectIcon sx={{ fontSize: 16 }} /> },
  skipped: { label: 'Skipped', color: AdminColors.textMuted, icon: <SkipNextIcon sx={{ fontSize: 16 }} /> },
  already_barcoded: { label: 'Already Barcoded', color: AdminColors.info, icon: <AlreadyBarcodedIcon sx={{ fontSize: 16 }} /> },
};

// ============================================================================
// Filter Options
// ============================================================================

const REVIEW_STATUS_OPTIONS: { value: ReviewFilterStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const REVIEW_TYPE_OPTIONS: { value: ReviewType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'barcode_add', label: 'Add Barcode' },
  { value: 'barcode_reassign', label: 'Reassign' },
  { value: 'new_product', label: 'New Product' },
];

const MISSION_STATUS_OPTIONS: { value: MissionFilterStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

const MISSION_SOURCE_OPTIONS: { value: MissionFilterSource; label: string }[] = [
  { value: 'all', label: 'All Sources' },
  { value: 'auto', label: 'Auto' },
  { value: 'manual', label: 'Manual' },
  { value: 'recon', label: 'Recon' },
];

// ============================================================================
// Main Component
// ============================================================================

interface InventoryDashboardProps {
  onBack?: () => void;
}

export default function InventoryDashboard({ onBack }: InventoryDashboardProps = {}) {
  const { colors, glassHeaderStyle, elevatedInputStyle } = useAdminTheme();
  // -- Tab state --
  const [activeTab, setActiveTab] = useState(0);

  // -- Review state --
  const [reviewStatus, setReviewStatus] = useState<ReviewFilterStatus>('pending');
  const [reviewType, setReviewType] = useState<ReviewType | 'all'>('all');
  const [reviewSearch, setReviewSearch] = useState('');
  const [reviews, setReviews] = useState<BarcodeReview[]>([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [reviewPage, setReviewPage] = useState(0);
  const reviewTopRef = useScrollToListTop(reviewPage);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // -- Mission state --
  const [missionStatus, setMissionStatus] = useState<MissionFilterStatus>('all');
  const [missionSource, setMissionSource] = useState<MissionFilterSource>('all');
  const [missions, setMissions] = useState<AuditMission[]>([]);
  const [missionTotal, setMissionTotal] = useState(0);
  const [missionPage, setMissionPage] = useState(0);
  const missionTopRef = useScrollToListTop(missionPage);
  const [missionLoading, setMissionLoading] = useState(false);
  const [missionError, setMissionError] = useState<string | null>(null);
  const [selectedMission, setSelectedMission] = useState<AuditMission | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // -- Recon state --
  const [reconStatus, setReconStatus] = useState<MissionFilterStatus>('all');
  const [reconMissions, setReconMissions] = useState<AuditMission[]>([]);
  const [reconTotal, setReconTotal] = useState(0);
  const [reconPage, setReconPage] = useState(0);
  const reconTopRef = useScrollToListTop(reconPage);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState<string | null>(null);
  const [expandedRecon, setExpandedRecon] = useState<string | null>(null);

  // -- Stats state --
  const [auditStats, setAuditStats] = useState<AuditStats | null>(null);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [approvedTodayCount, setApprovedTodayCount] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);

  // -- URL query tab support --
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'missions') setActiveTab(1);
    else if (tab === 'recon') setActiveTab(2);
  }, []);

  // ========================================================================
  // Data Fetching
  // ========================================================================

  const fetchReviews = useCallback(async () => {
    setReviewLoading(true);
    setReviewError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(reviewPage + 1));
      params.set('limit', '25');
      if (reviewStatus !== 'all') params.set('status', reviewStatus);
      if (reviewType !== 'all') params.set('reviewType', reviewType);
      if (reviewSearch.trim()) params.set('search', reviewSearch.trim());

      const result = await apiFetch<ReviewListResponse>(
        `/admin/product-barcode-reviews?${params.toString()}`
      );
      setReviews(result.data);
      setReviewTotal(result.total);
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Failed to fetch reviews');
    } finally {
      setReviewLoading(false);
    }
  }, [reviewStatus, reviewType, reviewSearch, reviewPage]);

  const fetchMissions = useCallback(async () => {
    setMissionLoading(true);
    setMissionError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(missionPage + 1));
      params.set('limit', '25');
      if (missionStatus !== 'all') params.set('status', missionStatus);
      if (missionSource !== 'all') params.set('source', missionSource);

      const result = await apiFetch<AuditMissionsResponse>(
        `/admin/barcode-audit/missions?${params.toString()}`
      );
      setMissions(result.data);
      setMissionTotal(result.total);
    } catch (err) {
      setMissionError(err instanceof Error ? err.message : 'Failed to fetch missions');
    } finally {
      setMissionLoading(false);
    }
  }, [missionStatus, missionSource, missionPage]);

  const fetchReconMissions = useCallback(async () => {
    setReconLoading(true);
    setReconError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(reconPage + 1));
      params.set('limit', '25');
      params.set('source', 'recon');
      if (reconStatus !== 'all') params.set('status', reconStatus);

      const result = await apiFetch<AuditMissionsResponse>(
        `/admin/barcode-audit/missions?${params.toString()}`
      );
      setReconMissions(result.data);
      setReconTotal(result.total);
    } catch (err) {
      setReconError(err instanceof Error ? err.message : 'Failed to fetch recon sessions');
    } finally {
      setReconLoading(false);
    }
  }, [reconStatus, reconPage]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [statsRes, pendingRes, approvedRes] = await Promise.all([
        apiFetch<{ data: AuditStats }>('/admin/barcode-audit/stats').catch(() => null),
        apiFetch<ReviewListResponse>('/admin/product-barcode-reviews?status=pending&page=1&limit=1').catch(() => null),
        apiFetch<ReviewListResponse>('/admin/product-barcode-reviews?status=approved&page=1&limit=1').catch(() => null),
      ]);
      if (statsRes) setAuditStats(statsRes.data);
      if (pendingRes) setPendingReviewCount(pendingRes.total);
      if (approvedRes) setApprovedTodayCount(approvedRes.total);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // -- Initial loads --
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchReviews(); }, [fetchReviews]);
  useEffect(() => { if (activeTab === 1) fetchMissions(); }, [fetchMissions, activeTab]);
  useEffect(() => { if (activeTab === 2) fetchReconMissions(); }, [fetchReconMissions, activeTab]);

  // Reset pages on filter changes
  useEffect(() => { setReviewPage(0); }, [reviewStatus, reviewType, reviewSearch]);
  useEffect(() => { setMissionPage(0); }, [missionStatus, missionSource]);
  useEffect(() => { setReconPage(0); }, [reconStatus]);

  // ========================================================================
  // Actions
  // ========================================================================

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      fetchStats(),
      fetchReviews(),
      activeTab === 1 ? fetchMissions() : Promise.resolve(),
      activeTab === 2 ? fetchReconMissions() : Promise.resolve(),
    ]);
  }, [fetchStats, fetchReviews, fetchMissions, fetchReconMissions, activeTab]);

  const handleApprove = useCallback(async (id: string) => {
    setProcessingId(id);
    try {
      await apiFetch(`/admin/product-barcode-reviews/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action: 'approve' }),
      });
      await Promise.all([fetchReviews(), fetchStats()]);
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setProcessingId(null);
    }
  }, [fetchReviews, fetchStats]);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    setProcessingId(rejectTarget);
    setRejectTarget(null);
    try {
      await apiFetch(`/admin/product-barcode-reviews/${rejectTarget}`, {
        method: 'PATCH',
        body: JSON.stringify({ action: 'reject', rejectionReason: rejectReason.trim() }),
      });
      setRejectReason('');
      await Promise.all([fetchReviews(), fetchStats()]);
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setProcessingId(null);
    }
  }, [rejectTarget, rejectReason, fetchReviews, fetchStats]);

  const handleMissionClick = useCallback(async (mission: AuditMission) => {
    setSelectedMission(mission);
    setDetailOpen(true);
    try {
      const detail = await apiFetch<{ data: AuditMission }>(`/admin/barcode-audit/missions/${mission.id}`);
      if (detail.data) setSelectedMission(detail.data);
    } catch { /* keep optimistic data */ }
  }, []);

  const handleCancelMission = useCallback(async (id: string) => {
    setCancelling(true);
    try {
      await apiFetch(`/admin/barcode-audit/missions/${id}/cancel`, { method: 'POST' });
      setDetailOpen(false);
      setSelectedMission(null);
      await Promise.all([fetchMissions(), fetchStats()]);
    } catch (err) {
      setMissionError(err instanceof Error ? err.message : 'Failed to cancel mission');
    } finally {
      setCancelling(false);
    }
  }, [fetchMissions, fetchStats]);

  const handleMissionCreated = useCallback(async () => {
    await Promise.all([fetchMissions(), fetchStats()]);
  }, [fetchMissions, fetchStats]);

  const handleReconExpand = useCallback(async (missionId: string) => {
    if (expandedRecon === missionId) {
      setExpandedRecon(null);
      return;
    }
    setExpandedRecon(missionId);
    // Fetch items if not loaded
    const mission = reconMissions.find(m => m.id === missionId);
    if (mission && !mission.items) {
      try {
        const detail = await apiFetch<{ data: AuditMission }>(`/admin/barcode-audit/missions/${missionId}`);
        if (detail.data) {
          setReconMissions(prev => prev.map(m => m.id === missionId ? detail.data : m));
        }
      } catch { /* ignore */ }
    }
  }, [expandedRecon, reconMissions]);

  // -- GlowCard click handlers --
  const handleCardClick = useCallback((tab: number, filter?: () => void) => {
    setActiveTab(tab);
    if (filter) filter();
  }, []);

  // ========================================================================
  // Derived stats
  // ========================================================================

  const activeMissionCount = auditStats?.activeMissions ?? 0;
  const reconSessionCount = reconTotal;
  const totalItemsScanned = auditStats?.totalItemsScanned ?? 0;

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Title title="Mission Control Centre" />

      {/* ================================================================ */}
      {/* Glassmorphism Header                                             */}
      {/* ================================================================ */}
      <Box sx={{ ...glassHeaderStyle, px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {onBack && (
              <IconButton onClick={onBack} sx={{ color: colors.textSecondary, mr: 0.5 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <DashboardIcon sx={{ fontSize: 30, color: colors.info }} />
            <Box>
              <Typography variant="h6" fontWeight={800} color={colors.textPrimary}>
                Mission Control Centre
              </Typography>
              <Typography variant="caption" color={colors.textSecondary}>
                Barcode reviews, audit missions, and stock reconciliation
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh all">
            <IconButton
              onClick={handleRefresh}
              sx={{
                color: colors.textSecondary,
                transition: 'transform 0.3s',
                '&:hover': { transform: 'rotate(180deg)', color: colors.info },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ px: 3 }}>
        {/* ================================================================ */}
        {/* Summary GlowCards                                                */}
        {/* ================================================================ */}
        <Grid container spacing={2} sx={{ mt: 2, mb: 1 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <GlowCard
              icon={<BarcodeIcon />}
              label="Pending Reviews"
              value={pendingReviewCount}
              color={colors.warning}
              pulse={pendingReviewCount > 0}
              onClick={() => handleCardClick(0, () => setReviewStatus('pending'))}
              active={activeTab === 0 && reviewStatus === 'pending'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <GlowCard
              icon={<ApproveIcon />}
              label="Approved"
              value={approvedTodayCount}
              color={colors.success}
              onClick={() => handleCardClick(0, () => setReviewStatus('approved'))}
              active={activeTab === 0 && reviewStatus === 'approved'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <GlowCard
              icon={<MissionIcon />}
              label="Active Missions"
              value={activeMissionCount}
              color={colors.info}
              pulse={activeMissionCount > 0}
              onClick={() => handleCardClick(1, () => { setMissionStatus('active'); setMissionSource('all'); })}
              active={activeTab === 1 && missionStatus === 'active'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <GlowCard
              icon={<ReconIcon />}
              label="Recon Sessions"
              value={reconSessionCount}
              color={colors.purple}
              onClick={() => handleCardClick(2)}
              active={activeTab === 2}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <GlowCard
              icon={<ScannedIcon />}
              label="Items Scanned"
              value={totalItemsScanned}
              color={colors.textPrimary}
            />
          </Grid>
        </Grid>

        {/* ================================================================ */}
        {/* Tabs                                                             */}
        {/* ================================================================ */}
        <Box sx={{ borderBottom: `1px solid ${colors.border}`, mt: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                color: colors.textMuted,
                fontWeight: 700,
                fontSize: '0.8rem',
                letterSpacing: '0.02em',
                textTransform: 'none',
                minHeight: 48,
                '&.Mui-selected': { color: colors.info },
              },
              '& .MuiTabs-indicator': { backgroundColor: colors.info, height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            <Tab
              icon={<BarcodeIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  Barcode Review
                  {pendingReviewCount > 0 && (
                    <Chip label={pendingReviewCount} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${colors.warning}20`, color: colors.warning }} />
                  )}
                </Box>
              }
            />
            <Tab
              icon={<MissionIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  Audit Missions
                  {activeMissionCount > 0 && (
                    <Chip label={activeMissionCount} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${colors.info}20`, color: colors.info }} />
                  )}
                </Box>
              }
            />
            <Tab
              icon={<ReconIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Stock Recon"
            />
          </Tabs>
        </Box>

        {/* ================================================================ */}
        {/* Tab 0: Barcode Review                                            */}
        {/* ================================================================ */}
        <TabPanel value={activeTab} index={0}>
          {/* Filter bar */}
          <Paper ref={reviewTopRef} sx={{ p: 2, mb: 3, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              {/* Status chips */}
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {REVIEW_STATUS_OPTIONS.map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    onClick={() => setReviewStatus(opt.value)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      bgcolor: reviewStatus === opt.value ? `${colors.info}20` : colors.bgElevated,
                      color: reviewStatus === opt.value ? colors.info : colors.textSecondary,
                      border: `1px solid ${reviewStatus === opt.value ? colors.info : colors.border}`,
                      '&:hover': { bgcolor: `${colors.info}15` },
                    }}
                  />
                ))}
              </Box>

              <Divider orientation="vertical" flexItem sx={{ borderColor: colors.border }} />

              {/* Review type chips */}
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {REVIEW_TYPE_OPTIONS.map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    onClick={() => setReviewType(opt.value)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      bgcolor: reviewType === opt.value ? `${colors.warning}20` : colors.bgElevated,
                      color: reviewType === opt.value ? colors.warning : colors.textSecondary,
                      border: `1px solid ${reviewType === opt.value ? colors.warning : colors.border}`,
                      '&:hover': { bgcolor: `${colors.warning}15` },
                    }}
                  />
                ))}
              </Box>

              {/* Search */}
              <TextField
                placeholder="Search product, SKU, barcode..."
                size="small"
                value={reviewSearch}
                onChange={e => setReviewSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} /></InputAdornment>,
                }}
                sx={{ ml: 'auto', minWidth: 240, ...elevatedInputStyle }}
              />

              <Chip
                label={`${reviewTotal} results`}
                size="small"
                sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: colors.bgElevated, color: colors.textMuted, border: `1px solid ${colors.border}` }}
              />
            </Box>
          </Paper>

          {/* Review cards */}
          {reviewError && (
            <Alert severity="error" sx={{ mb: 2, bgcolor: `${colors.error}15`, color: colors.error }}>{reviewError}</Alert>
          )}

          {reviewLoading && reviews.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress size={32} sx={{ color: colors.info }} />
              <Typography variant="body2" color={colors.textSecondary} sx={{ mt: 2 }}>Loading reviews...</Typography>
            </Box>
          ) : reviews.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <BarcodeIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
              <Typography variant="h6" color={colors.textSecondary}>
                {reviewStatus === 'pending' ? 'No pending barcode reviews' : 'No reviews found'}
              </Typography>
              <Typography variant="body2" color={colors.textMuted}>
                When pickers scan new barcodes, they&apos;ll appear here for review.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {reviews.map(review => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={review.id}>
                  <ReviewCardInline
                    review={review}
                    onApprove={handleApprove}
                    onReject={id => setRejectTarget(id)}
                    processing={processingId === review.id}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {reviewTotal > 25 && (
            <TablePagination
              component="div"
              count={reviewTotal}
              page={reviewPage}
              onPageChange={(_, p) => setReviewPage(p)}
              rowsPerPage={25}
              rowsPerPageOptions={[25]}
              sx={{ color: colors.textSecondary, borderTop: `1px solid ${colors.border}`, mt: 2 }}
            />
          )}
        </TabPanel>

        {/* ================================================================ */}
        {/* Tab 1: Audit Missions                                            */}
        {/* ================================================================ */}
        <TabPanel value={activeTab} index={1}>
          {/* Filter bar */}
          <Paper ref={missionTopRef} sx={{ p: 2, mb: 3, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {/* Status chips */}
              {MISSION_STATUS_OPTIONS.map(opt => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  onClick={() => setMissionStatus(opt.value)}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    bgcolor: missionStatus === opt.value ? `${colors.info}20` : colors.bgElevated,
                    color: missionStatus === opt.value ? colors.info : colors.textSecondary,
                    border: `1px solid ${missionStatus === opt.value ? colors.info : colors.border}`,
                    '&:hover': { bgcolor: `${colors.info}15` },
                  }}
                />
              ))}

              <Box sx={{ width: 1, borderLeft: `1px solid ${colors.border}`, height: 20, mx: 0.5 }} />

              {/* Source chips */}
              {MISSION_SOURCE_OPTIONS.map(opt => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  onClick={() => setMissionSource(opt.value)}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    bgcolor: missionSource === opt.value ? `${colors.warning}20` : colors.bgElevated,
                    color: missionSource === opt.value ? colors.warning : colors.textSecondary,
                    border: `1px solid ${missionSource === opt.value ? colors.warning : colors.border}`,
                    '&:hover': { bgcolor: `${colors.warning}15` },
                  }}
                />
              ))}

              <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`${missionTotal} missions`}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: colors.bgElevated, color: colors.textMuted, border: `1px solid ${colors.border}` }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}
                  sx={{ bgcolor: colors.info, fontWeight: 700, '&:hover': { bgcolor: '#0090CC' } }}
                >
                  Create Mission
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Mission cards */}
          {missionError && (
            <Alert severity="error" sx={{ mb: 2, bgcolor: `${colors.error}15`, color: colors.error }}>{missionError}</Alert>
          )}

          {missionLoading && missions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress size={32} sx={{ color: colors.info }} />
              <Typography variant="body2" color={colors.textSecondary} sx={{ mt: 2 }}>Loading missions...</Typography>
            </Box>
          ) : missions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <MissionIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
              <Typography variant="h6" color={colors.textSecondary}>
                {missionStatus === 'all' ? 'No audit missions yet' : `No ${missionStatus} missions`}
              </Typography>
              <Typography variant="body2" color={colors.textMuted} sx={{ mb: 3 }}>
                Missions are auto-assigned to idle pickers, or you can create one manually.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{ borderColor: colors.info, color: colors.info, fontWeight: 700, '&:hover': { borderColor: colors.info, bgcolor: `${colors.info}10` } }}
              >
                Create Manual Mission
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {missions.map(mission => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={mission.id}>
                  <MissionCardInline mission={mission} onClick={handleMissionClick} />
                </Grid>
              ))}
            </Grid>
          )}

          {missionTotal > 25 && (
            <TablePagination
              component="div"
              count={missionTotal}
              page={missionPage}
              onPageChange={(_, p) => setMissionPage(p)}
              rowsPerPage={25}
              rowsPerPageOptions={[25]}
              sx={{ color: colors.textSecondary, borderTop: `1px solid ${colors.border}`, mt: 2 }}
            />
          )}
        </TabPanel>

        {/* ================================================================ */}
        {/* Tab 2: Stock Recon                                               */}
        {/* ================================================================ */}
        <TabPanel value={activeTab} index={2}>
          {/* Filter bar */}
          <Paper ref={reconTopRef} sx={{ p: 2, mb: 3, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {[
                { value: 'all' as const, label: 'All' },
                { value: 'active' as const, label: 'Active' },
                { value: 'completed' as const, label: 'Completed' },
              ].map(opt => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  onClick={() => setReconStatus(opt.value)}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    bgcolor: reconStatus === opt.value ? `${colors.purple}20` : colors.bgElevated,
                    color: reconStatus === opt.value ? colors.purple : colors.textSecondary,
                    border: `1px solid ${reconStatus === opt.value ? colors.purple : colors.border}`,
                    '&:hover': { bgcolor: `${colors.purple}15` },
                  }}
                />
              ))}
              <Box sx={{ ml: 'auto' }}>
                <Chip
                  label={`${reconTotal} sessions`}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: colors.bgElevated, color: colors.textMuted, border: `1px solid ${colors.border}` }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Recon mission cards */}
          {reconError && (
            <Alert severity="error" sx={{ mb: 2, bgcolor: `${colors.error}15`, color: colors.error }}>{reconError}</Alert>
          )}

          {reconLoading && reconMissions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress size={32} sx={{ color: colors.purple }} />
              <Typography variant="body2" color={colors.textSecondary} sx={{ mt: 2 }}>Loading recon sessions...</Typography>
            </Box>
          ) : reconMissions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ReconIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
              <Typography variant="h6" color={colors.textSecondary}>No recon sessions found</Typography>
              <Typography variant="body2" color={colors.textMuted}>
                Stock reconciliation sessions from companion app will appear here.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {reconMissions.map(mission => (
                <ReconSessionCard
                  key={mission.id}
                  mission={mission}
                  expanded={expandedRecon === mission.id}
                  onToggle={() => handleReconExpand(mission.id)}
                />
              ))}
            </Box>
          )}

          {reconTotal > 25 && (
            <TablePagination
              component="div"
              count={reconTotal}
              page={reconPage}
              onPageChange={(_, p) => setReconPage(p)}
              rowsPerPage={25}
              rowsPerPageOptions={[25]}
              sx={{ color: colors.textSecondary, borderTop: `1px solid ${colors.border}`, mt: 2 }}
            />
          )}
        </TabPanel>
      </Box>

      {/* ================================================================ */}
      {/* Reject Dialog                                                     */}
      {/* ================================================================ */}
      <Dialog
        open={rejectTarget !== null}
        onClose={() => { setRejectTarget(null); setRejectReason(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, fontWeight: 800 }}>Reject Review</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color={colors.textSecondary} sx={{ mb: 2 }}>
            Please provide a reason for rejecting this barcode submission.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            placeholder="Rejection reason..."
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            error={rejectReason.length === 0 && rejectTarget !== null}
            sx={elevatedInputStyle}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setRejectTarget(null); setRejectReason(''); }} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!rejectReason.trim()}
            onClick={handleRejectConfirm}
            sx={{ bgcolor: colors.error, fontWeight: 700, '&:hover': { bgcolor: '#CC0030' } }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================ */}
      {/* Mission Detail Drawer                                             */}
      {/* ================================================================ */}
      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedMission(null); }}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 480 }, bgcolor: colors.bgDeepest, borderLeft: `1px solid ${colors.border}` },
        }}
      >
        {selectedMission && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" fontWeight={800} color={colors.textPrimary}>Mission Details</Typography>
                <Typography variant="caption" color={colors.textMuted} fontFamily="monospace">{selectedMission.id.slice(0, 8)}...</Typography>
              </Box>
              <IconButton onClick={() => { setDetailOpen(false); setSelectedMission(null); }} sx={{ color: colors.textSecondary }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Summary */}
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${colors.border}` }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  label={selectedMission.status.toUpperCase()}
                  size="small"
                  sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: `${(MISSION_STATUS_CONFIG[selectedMission.status]?.color || colors.info)}15`, color: MISSION_STATUS_CONFIG[selectedMission.status]?.color || colors.info }}
                />
                <Chip
                  label={selectedMission.source.toUpperCase()}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: colors.bgElevated, color: colors.textSecondary }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                <Typography variant="body2" color={colors.textSecondary}>
                  Picker: <strong style={{ color: colors.textPrimary }}>{formatName(selectedMission.picker)}</strong>
                </Typography>
                {selectedMission.creator && (
                  <Typography variant="body2" color={colors.textSecondary}>
                    Created by: <strong style={{ color: colors.textPrimary }}>{formatName(selectedMission.creator)}</strong>
                  </Typography>
                )}
              </Box>
              {/* Progress */}
              {(() => {
                const processed = selectedMission.scannedCount + selectedMission.skippedCount;
                const progress = selectedMission.totalItems > 0 ? (processed / selectedMission.totalItems) * 100 : 0;
                return (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color={colors.textSecondary}>Progress</Typography>
                      <Typography variant="caption" fontWeight={700} color={colors.textPrimary}>
                        {processed}/{selectedMission.totalItems} ({Math.round(progress)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{ height: 8, borderRadius: 4, bgcolor: colors.bgElevated, '& .MuiLinearProgress-bar': { bgcolor: colors.success, borderRadius: 4 } }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                      <Typography variant="caption" color={colors.success}>{selectedMission.scannedCount} scanned</Typography>
                      <Typography variant="caption" color={colors.error}>{selectedMission.skippedCount} skipped/oos</Typography>
                      <Typography variant="caption" color={colors.textMuted}>{selectedMission.totalItems - processed} remaining</Typography>
                    </Box>
                  </Box>
                );
              })()}
            </Box>

            {/* Items list */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={700} color={colors.textPrimary} sx={{ mb: 1.5 }}>
                Items ({selectedMission.items?.length || selectedMission.totalItems})
              </Typography>
              {selectedMission.items?.map(item => {
                const cfg = ITEM_STATUS_CONFIG[item.status];
                const productName = item.product?.name || 'Unknown Product';
                const productSku = item.product?.sku || '---';
                const thumbnail = item.product?.attachments?.[0]?.thumbnailUrl;
                return (
                  <Box
                    key={item.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1, borderRadius: 1.5, bgcolor: colors.bgCard, border: `1px solid ${colors.border}` }}
                  >
                    {thumbnail ? (
                      <Box component="img" src={thumbnail} sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover', bgcolor: colors.bgElevated }} />
                    ) : (
                      <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: colors.bgElevated, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BarcodeIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} color={colors.textPrimary} noWrap>{productName}</Typography>
                      <Typography variant="caption" color={colors.textMuted}>
                        {productSku}
                        {item.scannedBarcode && <> &middot; <span style={{ color: colors.info, fontFamily: 'monospace' }}>{item.scannedBarcode}</span></>}
                      </Typography>
                    </Box>
                    <Chip
                      icon={<Box sx={{ display: 'flex', color: cfg.color }}>{cfg.icon}</Box>}
                      label={cfg.label}
                      size="small"
                      sx={{ height: 24, fontWeight: 600, fontSize: '0.65rem', bgcolor: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30`, '& .MuiChip-icon': { ml: 0.5 } }}
                    />
                  </Box>
                );
              })}
              {!selectedMission.items?.length && (
                <Typography variant="body2" color={colors.textMuted} sx={{ textAlign: 'center', py: 4 }}>Loading items...</Typography>
              )}
            </Box>

            {/* Cancel action */}
            {['pending', 'active', 'paused'].includes(selectedMission.status) && (
              <Box sx={{ p: 2.5, borderTop: `1px solid ${colors.border}` }}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={cancelling}
                  onClick={() => handleCancelMission(selectedMission.id)}
                  sx={{ borderColor: colors.error, color: colors.error, fontWeight: 700, '&:hover': { borderColor: colors.error, bgcolor: `${colors.error}10` } }}
                >
                  {cancelling ? <CircularProgress size={20} sx={{ color: colors.error }} /> : 'Cancel Mission'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Drawer>

      {/* ================================================================ */}
      {/* Create Mission Dialog                                             */}
      {/* ================================================================ */}
      <CreateMissionDialogInline
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleMissionCreated}
      />
    </Box>
  );
}

// ============================================================================
// Inline Review Card
// ============================================================================

function ReviewCardInline({
  review,
  onApprove,
  onReject,
  processing,
}: {
  review: BarcodeReview;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  processing: boolean;
}) {
  const photoSrc = review.photoDownloadUrl || review.photoUrl;
  const isPending = review.status === 'pending';
  const reviewType = review.reviewType || 'barcode_add';
  const typeConfig = REVIEW_TYPE_CONFIG[reviewType] || REVIEW_TYPE_CONFIG.barcode_add;

  return (
    <Card
      sx={{
        bgcolor: AdminColors.bgCard,
        border: `1px solid ${AdminColors.border}`,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': { borderColor: typeConfig.color, boxShadow: `0 0 12px ${typeConfig.color}33` },
      }}
    >
      {photoSrc ? (
        <CardMedia component="img" height={200} image={photoSrc} alt={review.product?.name || review.suggestedProductName || 'Product photo'} sx={{ objectFit: 'cover', bgcolor: AdminColors.bgElevated }} />
      ) : (
        <Box sx={{ height: 200, bgcolor: AdminColors.bgElevated, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <BarcodeIcon sx={{ fontSize: 48, color: AdminColors.textMuted }} />
        </Box>
      )}

      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ mb: 1 }}>
          <Chip
            icon={typeConfig.icon as React.ReactElement}
            label={typeConfig.label}
            size="small"
            sx={{ bgcolor: `${typeConfig.color}15`, color: typeConfig.color, fontSize: '0.7rem', fontWeight: 700, border: `1px solid ${typeConfig.color}30` }}
          />
        </Box>

        <Typography variant="subtitle1" fontWeight={700} color={AdminColors.textPrimary} noWrap>
          {reviewType === 'new_product' ? review.suggestedProductName || 'New Product' : review.product?.name || 'Unknown Product'}
        </Typography>

        {review.product && (
          <Typography variant="caption" color={AdminColors.textSecondary} component="div">
            SKU: {review.product.sku || '\u2014'}{review.product.brand && ` \u00b7 ${review.product.brand}`}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <BarcodeIcon sx={{ fontSize: 16, color: AdminColors.info }} />
          <Typography variant="body2" fontWeight={600} color={AdminColors.info} fontFamily="monospace">{review.scannedBarcode}</Typography>
        </Box>

        {review.product?.barcode && (
          <Typography variant="caption" color={AdminColors.warning} component="div" sx={{ mt: 0.5 }}>
            Current barcode: {review.product.barcode}
          </Typography>
        )}

        {reviewType === 'barcode_reassign' && review.wrongProduct && (
          <Box sx={{ mt: 1, p: 1, bgcolor: `${AdminColors.warning}10`, borderRadius: 1, border: `1px solid ${AdminColors.warning}20` }}>
            <Typography variant="caption" color={AdminColors.warning} fontWeight={700} component="div">Wrong product (currently has barcode):</Typography>
            <Typography variant="caption" color={AdminColors.textSecondary} component="div">{review.wrongProduct.name} &middot; {review.wrongProduct.sku}</Typography>
          </Box>
        )}

        {review.quantityFound != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <ReconIcon sx={{ fontSize: 14, color: AdminColors.textSecondary }} />
            <Typography variant="caption" color={AdminColors.textSecondary}>Qty found: {review.quantityFound}</Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          <Chip icon={<PersonIcon sx={{ fontSize: 14 }} />} label={formatName(review.submitter)} size="small" sx={{ bgcolor: AdminColors.bgElevated, color: AdminColors.textSecondary, fontSize: '0.7rem' }} />
          {review.order ? (
            <Chip icon={<ReceiptLongIcon sx={{ fontSize: 14 }} />} label={`#${review.order.number}`} size="small" sx={{ bgcolor: AdminColors.bgElevated, color: AdminColors.textSecondary, fontSize: '0.7rem' }} />
          ) : review.auditItemId ? (
            <Chip icon={<MissionIcon sx={{ fontSize: 14 }} />} label="Recon Scan" size="small" sx={{ bgcolor: `${AdminColors.warning}15`, color: AdminColors.warning, fontSize: '0.7rem', border: `1px solid ${AdminColors.warning}30` }} />
          ) : null}
          <Typography variant="caption" color={AdminColors.textMuted} sx={{ alignSelf: 'center', ml: 'auto' }}>{timeAgo(review.createdAt)}</Typography>
        </Box>
      </CardContent>

      {isPending && (
        <CardActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<ApproveIcon />}
            disabled={processing}
            onClick={() => onApprove(review.id)}
            sx={{ bgcolor: AdminColors.success, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#00CC6E', boxShadow: `0 0 8px ${AdminColors.success}` } }}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RejectIcon />}
            disabled={processing}
            onClick={() => onReject(review.id)}
            sx={{ borderColor: AdminColors.error, color: AdminColors.error, fontWeight: 700, '&:hover': { borderColor: AdminColors.error, bgcolor: `${AdminColors.error}15` } }}
          >
            Reject
          </Button>
        </CardActions>
      )}

      {!isPending && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Chip
            label={review.status.toUpperCase()}
            size="small"
            sx={{
              bgcolor: review.status === 'approved' ? `${AdminColors.success}20` : `${AdminColors.error}20`,
              color: review.status === 'approved' ? AdminColors.success : AdminColors.error,
              fontWeight: 700,
            }}
          />
        </Box>
      )}
    </Card>
  );
}

// ============================================================================
// Inline Mission Card
// ============================================================================

function MissionCardInline({ mission, onClick }: { mission: AuditMission; onClick: (m: AuditMission) => void }) {
  const statusCfg = MISSION_STATUS_CONFIG[mission.status];
  const sourceCfg = MISSION_SOURCE_CONFIG[mission.source] || MISSION_SOURCE_CONFIG.manual;
  const processed = mission.scannedCount + mission.skippedCount;
  const progress = mission.totalItems > 0 ? (processed / mission.totalItems) * 100 : 0;
  const isTerminal = ['completed', 'expired', 'cancelled'].includes(mission.status);

  return (
    <Card
      sx={{
        bgcolor: AdminColors.bgCard,
        border: `1px solid ${AdminColors.border}`,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': { borderColor: statusCfg.color, boxShadow: `0 0 12px ${statusCfg.color}33` },
      }}
    >
      <CardActionArea onClick={() => onClick(mission)}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Chip label={statusCfg.label} size="small" sx={{ height: 22, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.03em', bgcolor: `${statusCfg.color}15`, color: statusCfg.color, border: `1px solid ${statusCfg.color}40` }} />
            <Chip label={sourceCfg.label} size="small" sx={{ height: 22, fontWeight: 600, fontSize: '0.65rem', bgcolor: `${sourceCfg.color}10`, color: sourceCfg.color, border: `1px solid ${sourceCfg.color}30` }} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <PersonIcon sx={{ fontSize: 16, color: AdminColors.textSecondary }} />
            <Typography variant="body2" fontWeight={600} color={AdminColors.textPrimary}>{formatName(mission.picker)}</Typography>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color={AdminColors.textSecondary}>{mission.scannedCount} scanned, {mission.skippedCount} skipped</Typography>
              <Typography variant="caption" fontWeight={700} color={AdminColors.textPrimary}>{processed}/{mission.totalItems}</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 6, borderRadius: 3, bgcolor: AdminColors.bgElevated, '& .MuiLinearProgress-bar': { bgcolor: isTerminal ? AdminColors.success : AdminColors.info, borderRadius: 3 } }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 14, color: AdminColors.textMuted }} />
            <Typography variant="caption" color={AdminColors.textMuted}>
              {isTerminal ? timeAgo(mission.completedAt || mission.updatedAt) : timeRemaining(mission.expiresAt)}
            </Typography>
            <Typography variant="caption" color={AdminColors.textMuted} sx={{ ml: 'auto' }}>{timeAgo(mission.createdAt)}</Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ============================================================================
// Recon Session Card (expandable)
// ============================================================================

function ReconSessionCard({ mission, expanded, onToggle }: { mission: AuditMission; expanded: boolean; onToggle: () => void }) {
  const statusCfg = MISSION_STATUS_CONFIG[mission.status];
  const processed = mission.scannedCount + mission.skippedCount;
  const progress = mission.totalItems > 0 ? (processed / mission.totalItems) * 100 : 0;

  // Item breakdown from loaded items
  const items = mission.items || [];
  const scannedItems = items.filter(i => i.status === 'scanned').length;
  const skippedItems = items.filter(i => i.status === 'skipped' || i.status === 'out_of_stock').length;
  const pendingItems = items.filter(i => i.status === 'pending').length;

  return (
    <Paper sx={{ bgcolor: AdminColors.bgCard, border: `1px solid ${AdminColors.border}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box
        sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: AdminColors.bgElevated } }}
        onClick={onToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip label={statusCfg.label} size="small" sx={{ height: 22, fontWeight: 700, fontSize: '0.65rem', bgcolor: `${statusCfg.color}15`, color: statusCfg.color, border: `1px solid ${statusCfg.color}40` }} />
              <Chip label="Recon" size="small" sx={{ height: 22, fontWeight: 600, fontSize: '0.65rem', bgcolor: `${AdminColors.purple}10`, color: AdminColors.purple, border: `1px solid ${AdminColors.purple}30` }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 16, color: AdminColors.textSecondary }} />
              <Typography variant="body2" fontWeight={600} color={AdminColors.textPrimary}>{formatName(mission.picker)}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" fontWeight={700} color={AdminColors.textPrimary}>
              {mission.scannedCount} items scanned
            </Typography>
            <Typography variant="caption" color={AdminColors.textMuted}>{timeAgo(mission.createdAt)}</Typography>
            {expanded ? <ExpandLessIcon sx={{ color: AdminColors.textMuted }} /> : <ExpandMoreIcon sx={{ color: AdminColors.textMuted }} />}
          </Box>
        </Box>

        {/* Progress bar */}
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color={AdminColors.textSecondary}>{mission.scannedCount} scanned, {mission.skippedCount} skipped</Typography>
            <Typography variant="caption" fontWeight={700} color={AdminColors.textPrimary}>{processed}/{mission.totalItems}</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 6, borderRadius: 3, bgcolor: AdminColors.bgElevated, '& .MuiLinearProgress-bar': { bgcolor: AdminColors.purple, borderRadius: 3 } }}
          />
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${AdminColors.border}` }}>
          {items.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress size={20} sx={{ color: AdminColors.purple }} />
              <Typography variant="caption" color={AdminColors.textMuted} sx={{ ml: 1 }}>Loading items...</Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 1.5 }}>
              {items.map(item => {
                const cfg = ITEM_STATUS_CONFIG[item.status];
                const productName = item.product?.name || 'Unknown Product';
                const productSku = item.product?.sku || '---';
                const thumbnail = item.product?.attachments?.[0]?.thumbnailUrl;
                return (
                  <Box
                    key={item.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1, borderRadius: 1.5, bgcolor: AdminColors.bgDeepest, border: `1px solid ${AdminColors.border}` }}
                  >
                    {thumbnail ? (
                      <Box component="img" src={thumbnail} sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', bgcolor: AdminColors.bgElevated }} />
                    ) : (
                      <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: AdminColors.bgElevated, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BarcodeIcon sx={{ fontSize: 16, color: AdminColors.textMuted }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} color={AdminColors.textPrimary} noWrap>{productName}</Typography>
                      <Typography variant="caption" color={AdminColors.textMuted}>
                        {productSku}
                        {item.scannedBarcode && <> &middot; <span style={{ color: AdminColors.info, fontFamily: 'monospace' }}>{item.scannedBarcode}</span></>}
                      </Typography>
                    </Box>
                    <Chip
                      icon={<Box sx={{ display: 'flex', color: cfg.color }}>{cfg.icon}</Box>}
                      label={cfg.label}
                      size="small"
                      sx={{ height: 22, fontWeight: 600, fontSize: '0.6rem', bgcolor: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30`, '& .MuiChip-icon': { ml: 0.5 } }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ============================================================================
// Inline Create Mission Dialog
// ============================================================================

function CreateMissionDialogInline({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [pickers, setPickers] = useState<OnlinePicker[]>([]);
  const [selectedPicker, setSelectedPicker] = useState('');
  const [missionSize, setMissionSize] = useState(10);
  const [loadingPickers, setLoadingPickers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPickers = useCallback(async () => {
    setLoadingPickers(true);
    try {
      const result = await apiFetch<{ data: OnlinePicker[] }>('/admin/barcode-audit/candidates/pickers');
      setPickers(result.data || []);
    } catch {
      setPickers([]);
    } finally {
      setLoadingPickers(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchPickers();
      setSelectedPicker('');
      setMissionSize(10);
      setError(null);
    }
  }, [open, fetchPickers]);

  const handleCreate = async () => {
    if (!selectedPicker) { setError('Please select a picker'); return; }
    setCreating(true);
    setError(null);
    try {
      const picker = pickers.find(p => p.id === selectedPicker);
      await apiFetch('/admin/barcode-audit/missions', {
        method: 'POST',
        body: JSON.stringify({ pickerId: selectedPicker, storeId: picker?.storeId, missionSize }),
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create mission');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: AdminColors.bgCard, border: `1px solid ${AdminColors.border}`, borderRadius: 2 } }}
    >
      <DialogTitle sx={{ color: AdminColors.textPrimary, fontWeight: 800 }}>Create Audit Mission</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: `${AdminColors.error}15`, color: AdminColors.error }}>{error}</Alert>
        )}
        <Typography variant="body2" color={AdminColors.textSecondary} sx={{ mb: 3 }}>
          Assign a barcode audit mission to a picker. Products without barcodes that are in stock at the picker&apos;s store will be auto-selected.
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel sx={{ color: AdminColors.textMuted }}>Select Picker</InputLabel>
          <Select
            value={selectedPicker}
            onChange={e => setSelectedPicker(e.target.value)}
            label="Select Picker"
            disabled={loadingPickers}
            startAdornment={
              loadingPickers ? <CircularProgress size={16} sx={{ color: AdminColors.info, mr: 1 }} /> : <PersonIcon sx={{ color: AdminColors.textMuted, mr: 1 }} />
            }
            sx={{
              color: AdminColors.textPrimary,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: AdminColors.border },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: AdminColors.borderGlow },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AdminColors.info },
            }}
          >
            {pickers.length === 0 && !loadingPickers && (
              <MenuItem disabled><Typography variant="body2" color={AdminColors.textMuted}>No pickers available</Typography></MenuItem>
            )}
            {pickers.map(picker => (
              <MenuItem key={picker.id} value={picker.id}>
                <Box>
                  <Typography variant="body2">{picker.firstName} {picker.lastName}</Typography>
                  <Typography variant="caption" color={AdminColors.textMuted}>{picker.storeName}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Products Per Mission"
          type="number"
          fullWidth
          value={missionSize}
          onChange={e => { const val = Math.max(1, Math.min(50, parseInt(e.target.value) || 1)); setMissionSize(val); }}
          inputProps={{ min: 1, max: 50 }}
          InputProps={{ endAdornment: <InputAdornment position="end">items</InputAdornment> }}
          helperText="Number of products to include in the mission (1-50)"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: AdminColors.textPrimary,
              '& fieldset': { borderColor: AdminColors.border },
              '&:hover fieldset': { borderColor: AdminColors.borderGlow },
              '&.Mui-focused fieldset': { borderColor: AdminColors.info },
            },
            '& .MuiInputLabel-root': { color: AdminColors.textMuted },
            '& .MuiInputLabel-root.Mui-focused': { color: AdminColors.info },
            '& .MuiFormHelperText-root': { color: AdminColors.textMuted },
            '& .MuiInputAdornment-root': { color: AdminColors.textMuted },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: AdminColors.textSecondary }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || !selectedPicker}
          sx={{ bgcolor: AdminColors.info, fontWeight: 700, '&:hover': { bgcolor: '#0090CC' } }}
        >
          {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create Mission'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
